import { useParams, Link } from "react-router-dom";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { SEO } from "@/components/SEO";
import { useBlogPost } from "@/hooks/useBlogPosts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, ArrowLeft, User, Edit } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { format } from "date-fns";
import { Skeleton } from "@/components/ui/skeleton";
import { renderMarkdown } from "@/components/blog/MarkdownRenderer";
import { SuggestedPosts } from "@/components/blog/SuggestedPosts";

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading } = useBlogPost(slug || "");
  const { isAdmin } = useAdminAuth();

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="py-20 container px-4 md:px-6 max-w-3xl mx-auto">
          <Skeleton className="h-10 w-3/4 mb-4" />
          <Skeleton className="h-4 w-1/2 mb-8" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </PublicLayout>
    );
  }

  if (!post) {
    return (
      <PublicLayout>
        <div className="py-20 container px-4 md:px-6 text-center">
          <h1 className="text-3xl font-bold mb-4">Post Not Found</h1>
          <Button asChild variant="outline">
            <Link to="/blog">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Link>
          </Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEO
        title={post.title}
        description={post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogType="article"
        ogImage={post.cover_image_url || undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: post.excerpt,
          image: post.cover_image_url || undefined,
          author: { "@type": "Person", name: post.author_name },
          datePublished: post.published_at,
          dateModified: post.updated_at,
          publisher: { "@type": "Organization", name: "Reqqy" },
        }}
      />
      <article className="py-20 md:py-28">
        <div className="container px-4 md:px-6 max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
            {isAdmin && (
              <Button asChild variant="outline" size="sm">
                <Link to={`/blog/${post.slug}/edit`}>
                  <Edit className="w-4 h-4 mr-1" />
                  Edit Post
                </Link>
              </Button>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.map((tag: string) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-4 leading-tight">
            {post.title}
          </h1>

          <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
            {post.excerpt}
          </p>

          {post.cover_image_url && (
            <div className="w-full rounded-xl overflow-hidden mb-10 bg-background dark:bg-muted">
              <img
                src={post.cover_image_url}
                alt={post.title}
                className="w-full max-h-[500px] object-contain mx-auto"
                loading="lazy"
              />
            </div>
          )}

          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-10 pb-10 border-b border-border">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {post.author_name}
            </span>
            {post.published_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {format(new Date(post.published_at), "MMMM d, yyyy")}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {Math.ceil(post.content.split(" ").length / 200)} min read
            </span>
          </div>

          <div className="prose-custom">{renderMarkdown(post.content)}</div>

          {/* Suggested Posts */}
          <SuggestedPosts currentPost={post} />

          <div className="mt-16 pt-8 border-t border-border text-center">
            <h3 className="text-xl font-semibold mb-3">Ready to improve your requirements?</h3>
            <p className="text-muted-foreground mb-6">
              Try Reqqy free and see how clear your requirements really are.
            </p>
            <Button asChild className="gradient-primary text-primary-foreground">
              <Link to="/auth">Get Started Free</Link>
            </Button>
          </div>
        </div>
      </article>
    </PublicLayout>
  );
}
